import { getVariantConfig } from './appVariant'

const routes = {
    home: '/apps',
    import: '/import',
    landing: '/',
    subscription: '/subscription',
    upgrade: '/upgrade',
    userGuide:
        'https://support.noduslabs.com/hc/en-us/categories/360002512819-How-to-Use-InfraNodus',
    settings: '/settings',
    apiAccess: '/api-access',
    adminPanel: '/admin-panel',
    support: 'https://support.noduslabs.com/hc/en-us',
    joinCommunity: 'https://discord.gg/jdKWDwF98R',
    switchAccount: '/switch',
    get infranodus() {
        return getVariantConfig().brand.marketingDomain
    },
    get aboutInfranodus() {
        return getVariantConfig().brand.aboutUrl
    },
    methodology: 'https://dl.acm.org/doi/10.1145/3308558.3314123',
    logout: '/logout',
    login: '/login',
    signup: '/signup',
    synthesis: '/:userName/edit?showcontexts=1',
    conceptsSynthesis: '/:userName/edit?showconcepts=1',
    search: '/:userName/find',
    showAllUrl: username => `/${username}/edit?showcontexts=1`,
    showAllUrlPublic: username => `/${username}/?showcontexts=1`,
    showAllConceptsUrl: username => `/${username}/edit?showconcepts=1`,
    showAllConceptsUrlPublic: username => `/${username}/?showconcepts=1`,
    findAllUrl: username => `/${username}/find`,
    main: '/:mainPage',
    secondary: '/:mainPage/:secondPage',
    graph: '/:userName/:graphName/*',
}

export default routes

export const constructGraphComparisonUrl = (
    sourceContext,
    username,
    operation,
    contexts,
    isPublic
) => {
    if (contexts.length === 0)
        throw new Error(
            'Can not construct url for comparison if related contexts are not provided'
        )
    const contextsUrlQuery = contexts
        .map(contextName => `&addcontext=${contextName}`)
        .join('')

    const editorUrl = isPublic ? '' : 'edit'
    switch (operation) {
        case 'INTERSECTION':
            return `/${username}/${sourceContext}/${editorUrl}${contextsUrlQuery.replace(
                '&',
                '?'
            )}`
        case 'DIFFERENCE':
            return `/${username}/${sourceContext}/${editorUrl}?highlight=1&missing=1${contextsUrlQuery}`
        case 'COMBINATION':
            return `/${username}/${sourceContext}/${editorUrl}?merge=1${contextsUrlQuery}`
        case 'EMBED':
            return `/${username}/${sourceContext}/${editorUrl}?interpret=1${contextsUrlQuery}`
    }
}

export const getContextRoute = (
    label,
    defaultRevisionUrl = '',
    contextType,
    username,
    isPublicView
) => {
    // Non-owner viewers must never get a `/edit`-style suffix: the
    // `/:user/:context/:pathapp` route is JWT-gated and will redirect them
    // to /login. View mode is also encoded in the query string, so dropping
    // the suffix loses nothing for read-only viewers.
    let editUrl = 'edit'
    switch (contextType) {
        case 'META':
            if (isPublicView) editUrl = ''
            return `${username}/${label}/${editUrl}?interpret=1&addcontext=${getConnectedContext(
                label,
                'meta_'
            )}`
        case 'SEO':
            editUrl = isPublicView ? '' : 'seo'
            const connectedGraphName = getConnectedContext(label, 'seo_')
            return `${username}/serp_${connectedGraphName}/${editUrl}?highlight=1&addcontext=${label}&addcontext=kwrds_${connectedGraphName}&missing=1`
        case 'NICHE':
            editUrl = isPublicView ? '' : 'niche'
            const comparedGraphName = getConnectedContext(label, 'niche_')
            return `${username}/serp_${comparedGraphName}/${editUrl}?highlight=1&addcontext=${label}&addcontext=kwrds_${comparedGraphName}&missing=1`
        case 'GOOGLE':
        case 'MINDMAP':
        case 'TWITTER':
        case 'WIKILINKS':
        case 'WORDCLOUD':
            editUrl = isPublicView ? '' : contextType.toLowerCase()
            return addDefaultRevisionUrlParam(
                `${username}/${label}/${editUrl}`,
                defaultRevisionUrl
            )
        default:
            if (isPublicView) editUrl = ''

            // TODO move this to context type above when we integrate this logic into the backend
            const contextNamePrefix = label.split('_')[0]

            switch (contextNamePrefix) {
                case 'kwrds':
                    if (!isPublicView) editUrl = 'kwrds'
                    break
            }

            return addDefaultRevisionUrlParam(
                `${username}/${label}/${editUrl}`,
                defaultRevisionUrl
            )
    }
}

const addDefaultRevisionUrlParam = (url, defaultRevisionUrl) => {
    if (defaultRevisionUrl) {
        return `${url}?${defaultRevisionUrl}`
    }
    return url
}

const getConnectedContext = (label, prefix) => {
    return label.split(prefix)[1]
}

export const getComparedContexts = () => {
    if (!window || !window.location || !window.location.search) return []
    return new URLSearchParams(window.location.search).getAll('addcontext')
}

export const constructAppsUrl = (username, app) => {
    const longDateString = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    switch (app) {
        case 'default':
            return `/${username}/text_${longDateString}/edit`
        case 'ideation':
            return `/${username}/ideas_${longDateString}/edit?mode=insight`
        case 'analysis':
            return `/${username}/text_${longDateString}/notes`
        case 'mindmapping':
            return `/${username}/mindmap_${longDateString}/mindmap`
        case 'google':
            return `/${username}/google_${longDateString}/google`
        case 'apps':
            return `/apps`
    }
}
